import React, { FunctionComponent, useState, useEffect } from 'react';
import { FormikProps } from 'formik';
import { debounce } from 'debounce';
import { find, map } from 'ramda';
import { connect } from 'react-redux';
import Highlighter from 'react-highlight-words';
import { formatPhoneNumber } from '../../../../common/helpers/phoneHelper';
import { getHint } from '../Step2/utils';
import { IRefReqContact } from '../../../../common/models/contact/IRefReqContact';
import { IBindingCallback1, IBindingAction } from '../../../../common/models/callback';
import { IRefReqContactFilter } from '../../../../common/models/filter/IRefReqContactFilter';
import { ISelectedPosition } from '../../../../common/models/position/ISelectedPosition';
import { IRefReqSelectedContact } from '../../../../common/models/contact/IRefReqSelectedContact';
import { fetchReqBuilderContactsRoutine, resetReqBuilderContactsRoutine } from '../../routines';
import { IAppState } from '../../../../common/models/store/IAppState';
import KeyNavigationList from '../../../../components/KeyNavigationList';
import NavigationFooter from '../../../../components/NavigationFooter';
import Icon from '../../../../components/Icon';
import styles from './styles.module.scss';
import AddContactForm from '../../components/AddContactForm';
import { INewContactInfo } from '../../../../common/models/contact/INewContactInfo';
import { initialFormValues } from '../../components/AddContactForm/utils';
import { usePrevious } from '../../../../common/helpers/customHooks';

interface IProps extends FormikProps<INewContactInfo> {
  selectedPosition: ISelectedPosition | null;
  total: number;
  contacts: IRefReqContact[];
  candidates: IRefReqSelectedContact[];
  addCandidate: IBindingCallback1<IRefReqSelectedContact>;
  fetchContacts: IBindingCallback1<IRefReqContactFilter>;
  resetContacts: IBindingAction;
}

const Step2Form: FunctionComponent<IProps> = ({
  total,
  contacts,
  candidates,
  addCandidate,
  fetchContacts,
  resetContacts,
  ...props
}) => {
  const { values, submitForm, setValues } = props;
  const { firstName, lastName, email, phoneNumber } = values;
  const search = debounce((searchFilter: IRefReqContactFilter) => fetchContacts(searchFilter), 500);
  const isSearching = firstName.trim() || lastName.trim() || email?.trim() || phoneNumber?.trim();
  const defaultFilter = { from: 0, to: 10 };
  const [focusedField, setFocusedField] = useState('');
  const [isFormSubmitting, setIsSubmitting] = useState(false);

  const onClearAll = () => setValues(initialFormValues);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onSelect = ({ lastRole, ...item }: IRefReqContact) => {
    addCandidate(item);
    onClearAll();
  };

  const onBlur = (isEmail?: boolean) => {
    if (isEmail) {
      const matchingRow = find((row: IRefReqContact) => row.email === email)(contacts);
      if (matchingRow) {
        onSelect(matchingRow);
      }
    }
    setFocusedField('');
  };

  const onFocus = (val: keyof INewContactInfo) => setFocusedField(val);

  const onLoadMore = (from: number, to: number) => fetchContacts({
    from, to, ...values, phoneNumber: values.phoneNumber || '', email: values.email || ''
  });

  const onSubmit = () => {
    setIsSubmitting(true);
    submitForm();
  };

  const getListElements = () => map(item => (
    <div className={styles.listItem} role="presentation" onMouseDown={() => onSelect(item)}>
      <div>
        <div className="d-flex align-items-baseline mb-2">
          <span className="avenir-font">
            <Highlighter
              highlightClassName="highlighted-text"
              searchWords={[values.firstName.trim()]}
              textToHighlight={item.firstName}
              autoEscape
            />
            &nbsp;
            <Highlighter
              highlightClassName="highlighted-text"
              searchWords={[values.lastName.trim()]}
              textToHighlight={item.lastName}
              autoEscape
            />
          </span>
          {item.lastRole && <span className={styles.roleHint}>{getHint(item.lastRole)}</span>}
        </div>
        <div className="t-14 d-flex flex-column">
          {item.phoneNumber && (
            <Highlighter
              highlightClassName="highlighted-text"
              searchWords={[values.phoneNumber || '']}
              textToHighlight={formatPhoneNumber(item.phoneNumber)}
              autoEscape
            />
          )}
          {item.email && (
            <Highlighter
              highlightClassName="highlighted-text"
              searchWords={[values.email?.trim() || '']}
              textToHighlight={item.email}
              autoEscape
            />
          )}
        </div>
      </div>
      <div className={styles.squareIcon}>
        <Icon icon="plus" />
      </div>
    </div>
  ), contacts);

  const itemsHeight = map(({ phoneNumber: pn, email: e }) => (pn && e ? 92 : 82), contacts);

  const getItemSize = (i: number) => itemsHeight[i];

  useEffect(() => {
    if (isSearching) {
      search({ ...defaultFilter, ...values, phoneNumber: values.phoneNumber || '', email: values.email || '' });
    } else {
      resetContacts();
    }

    return () => search.clear();
  }, [values, candidates]);

  const prevCandidates = usePrevious(candidates);
  useEffect(() => {
    if (!prevCandidates || prevCandidates.length <= candidates.length) {
      setValues(initialFormValues);
      setIsSubmitting(false);
    }
  }, [candidates]);

  return (
    <>
      <AddContactForm
        isFormSubmitting={isFormSubmitting}
        onSubmit={onSubmit}
        onBlur={onBlur}
        onFocus={onFocus}
        buttonText={{
          id: 'addCandidate',
          defaultMessage: 'Add candidate'
        }}
        {...props}
      />
      {Boolean(focusedField) && Boolean(contacts.length) && (
        <div className={styles.list}>
          <KeyNavigationList
            getItemHeight={getItemSize}
            total={total}
            loadMore={onLoadMore}
            items={getListElements()}
            onSelect={index => onSelect(contacts[index])}
            footer={<NavigationFooter showCursor showEnter showTab />}
          />
        </div>
      )}
    </>
  );
};

const mapStateToProps = ({
  requestBuilder: { selectedPosition, contacts, totalContacts, candidates }
}: IAppState) => ({ selectedPosition, contacts, total: totalContacts, candidates });

const mapDispatchToProps = {
  fetchContacts: fetchReqBuilderContactsRoutine,
  resetContacts: resetReqBuilderContactsRoutine
};

export default connect(mapStateToProps, mapDispatchToProps)(Step2Form);
